import {
  Compass,
  Gamepad2,
  Home,
  LogIn,
  LogOut,
  Package,
  Settings,
  Users,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { PanelId } from '../types';
import { useAuth } from '../context/AuthContext';

interface SidebarProps {
  active: PanelId;
  onNavigate: (panel: PanelId) => void;
  onAuth: (mode: 'login' | 'register') => void;
}

const navItems: Array<{ id: PanelId; label: string; icon: typeof Home; auth?: boolean }> = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'discover', label: 'Discover', icon: Compass },
  { id: 'friends', label: 'Friends', icon: Users, auth: true },
  { id: 'inventory', label: 'Inventory', icon: Package, auth: true },
  { id: 'settings', label: 'Settings', icon: Settings, auth: true },
];

export function Sidebar({ active, onNavigate, onAuth }: SidebarProps) {
  const { user, logout } = useAuth();

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-white/10 p-4">
      <div className="mb-8 flex items-center gap-3 px-2">
        <img src="/korvex.svg" alt="Korvex" className="h-10 w-10 rounded-xl" />
        <div>
          <p className="font-display text-lg font-bold tracking-tight">Korvex</p>
          <p className="text-xs text-korvex-muted">playkorvex.com</p>
        </div>
      </div>

      <nav className="flex flex-1 flex-col gap-1">
        {navItems.map(({ id, label, icon: Icon, auth }) => {
          if (auth && !user) return null;
          const isActive = active === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onNavigate(id)}
              className={`nav-item relative ${isActive ? 'nav-item-active' : ''}`}
            >
              {isActive && (
                <motion.span
                  layoutId="nav-indicator"
                  className="absolute left-0 h-8 w-1 rounded-r-full bg-korvex-accent"
                />
              )}
              <Icon className="h-5 w-5" />
              {label}
            </button>
          );
        })}
        {user && (
          <button
            type="button"
            onClick={() => onNavigate('profile')}
            className={`nav-item ${active === 'profile' ? 'nav-item-active' : ''}`}
          >
            <span
              className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
              style={{ backgroundColor: user.avatarColor }}
            >
              {user.username.slice(0, 1).toUpperCase()}
            </span>
            Profile
          </button>
        )}
      </nav>

      <div className="mt-auto space-y-2 border-t border-white/10 pt-4">
        {user ? (
          <>
            <div className="rounded-xl bg-white/5 px-3 py-2">
              <p className="truncate text-sm font-semibold">{user.username}</p>
              {user.isAdmin && (
                <p className="text-xs font-medium text-korvex-accent">Platform Admin</p>
              )}
            </div>
            <button type="button" className="btn-ghost w-full" onClick={() => void logout()}>
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-primary w-full" onClick={() => onAuth('register')}>
              Get started
            </button>
            <button type="button" className="btn-ghost w-full" onClick={() => onAuth('login')}>
              <LogIn className="h-4 w-4" />
              Sign in
            </button>
          </>
        )}
      </div>
    </aside>
  );
}

export function MobileNav({ active, onNavigate }: Pick<SidebarProps, 'active' | 'onNavigate'>) {
  const items = [
    { id: 'home' as PanelId, icon: Home },
    { id: 'discover' as PanelId, icon: Compass },
    { id: 'friends' as PanelId, icon: Users },
    { id: 'inventory' as PanelId, icon: Package },
    { id: 'settings' as PanelId, icon: Settings },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-korvex-bg/90 backdrop-blur-xl md:hidden">
      <div className="flex justify-around px-2 py-2">
        {items.map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => onNavigate(id)}
            className={`rounded-xl p-3 ${active === id ? 'text-korvex-accent' : 'text-korvex-muted'}`}
          >
            <Icon className="h-5 w-5" />
          </button>
        ))}
      </div>
    </div>
  );
}

export function HeroBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-korvex-accent/30 bg-korvex-accent/10 px-3 py-1 text-xs font-semibold text-korvex-accent">
      <Gamepad2 className="h-3.5 w-3.5" />
      UGC Game Platform
    </span>
  );
}
